// CommercialTermsBar — buyer-requested terms (reply-by / validity / currency
// / incoterms / payment).
//
// Design — the section is COLLAPSED by default with a quiet summary line +
// edit pencil. Click pencil → reveals the 5 inline editable fields. Inside,
// each field is itself click-to-edit so the UI stays calm at rest and only
// surfaces controls when the buyer actually wants to change something.
//
// Values flow into the email body via Handlebars template variables. All
// fields optional; templates that don't reference them render unchanged.

import React, { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  EditOutlined as EditIcon,
  CheckOutlined as DoneIcon,
} from '@mui/icons-material';

export interface CommercialTerms {
  replyByDate?: string;
  validityDays?: number;
  currency?: string;
  incoterms?: string;
  paymentTerms?: string;
}

export const TENANT_DEFAULTS: CommercialTerms = {
  validityDays: 30,
  currency: 'USD',
  incoterms: 'DDP',
  paymentTerms: 'Net 30',
};

const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'ILS', 'JPY', 'CHF', 'CAD'];
const INCOTERMS_OPTIONS = ['DDP', 'DAP', 'FOB', 'CIF', 'EXW', 'FCA'];
const PAYMENT_OPTIONS = ['Net 30', 'Net 60', 'Net 90', 'COD', 'Prepayment', 'Letter of Credit'];
const VALIDITY_OPTIONS = [15, 30, 60, 90];

function defaultReplyByDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** The terms that apply: what the buyer set, over the defaults. This is what the email states. */
/** `defaults` are the company's usual terms (its sourcing profile) when known. */
export function effectiveCommercialTerms(value: CommercialTerms, defaults: CommercialTerms = TENANT_DEFAULTS): Required<CommercialTerms> {
  return {
    replyByDate: value.replyByDate ?? defaultReplyByDate(),
    validityDays: value.validityDays ?? defaults.validityDays ?? TENANT_DEFAULTS.validityDays!,
    currency: value.currency ?? defaults.currency ?? TENANT_DEFAULTS.currency!,
    incoterms: value.incoterms ?? defaults.incoterms ?? TENANT_DEFAULTS.incoterms!,
    paymentTerms: value.paymentTerms ?? defaults.paymentTerms ?? TENANT_DEFAULTS.paymentTerms!,
  };
}

interface CommercialTermsBarProps {
  value: CommercialTerms;
  onChange: (next: CommercialTerms) => void;
  supplierName?: string;
  /** Inside an edit panel that already names the section: the editor only, always open. */
  embedded?: boolean;
  /** The company's usual terms (its sourcing profile), shown when the buyer has not set one. */
  defaults?: CommercialTerms;
}

const CommercialTermsBar: React.FC<CommercialTermsBarProps> = ({
  value,
  onChange,
  supplierName: _supplierName,
  embedded = false,
  defaults,
}) => {
  const theme = useTheme();
  const [open, setExpanded] = useState(false);
  const expanded = embedded || open;
  const [editingField, setEditingField] = useState<keyof CommercialTerms | null>(null);

  // Layered defaults — tenant defaults + reply-by computed at render time
  const v = effectiveCommercialTerms(value, defaults);

  const buyerHasSet =
    value.replyByDate !== undefined ||
    value.validityDays !== undefined ||
    value.currency !== undefined ||
    value.incoterms !== undefined ||
    value.paymentTerms !== undefined;

  const set = (k: keyof CommercialTerms, val: any) => {
    if (val === '' || val == null) {
      const { [k]: _drop, ...rest } = value;
      void _drop;
      onChange(rest);
    } else {
      onChange({ ...value, [k]: val });
    }
    setEditingField(null);
  };

  // Compact summary line shown when collapsed
  const summaryLine = `Reply by ${formatDate(v.replyByDate)} · ${v.validityDays}-day validity · ${v.currency} · ${v.incoterms} · ${v.paymentTerms}`;

  return (
    <Box>
      {/* Header row — title on the left, status microcopy on the right.
          When expanded, the right side becomes a "Done" affordance so the
          buyer has a clear way to collapse the section back without hunting
          for the pencil that's now hidden with the summary card. */}
      {!embedded && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.7rem',
              letterSpacing: 0.7,
              textTransform: 'uppercase',
              color: theme.palette.text.primary,
              fontWeight: 800,
            }}
          >
            Commercial terms
          </Typography>
          {expanded ? (
            <Box
              role="button"
              tabIndex={0}
              onClick={() => {
                setExpanded(false);
                setEditingField(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setExpanded(false);
                  setEditingField(null);
                }
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                color: 'primary.main',
                fontSize: '0.7rem',
                fontWeight: 600,
                px: 0.75,
                py: 0.25,
                borderRadius: 0.75,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <DoneIcon sx={{ fontSize: 14 }} /> Done
            </Box>
          ) : (
            <Typography
              variant="caption"
              sx={{ fontSize: '0.68rem', color: 'text.disabled', fontStyle: 'italic' }}
            >
              {buyerHasSet ? 'Buyer-set' : 'Using defaults · optional'}
            </Typography>
          )}
        </Stack>
      )}

      {/* Collapsed summary card — ONLY rendered when not expanded. The 5
          editable field cards below replace it when the buyer is actively
          editing; no need to show the same values twice. */}
      {!expanded && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            alignItems: 'center',
            gap: 1.25,
            px: 1.75,
            py: 1.25,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5,
            bgcolor: 'background.paper',
            transition: 'background 160ms ease',
            '&:hover': {
              bgcolor: alpha(theme.palette.text.primary, 0.012),
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.82rem',
              color: 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={summaryLine}
          >
            {summaryLine}
          </Typography>
          <Tooltip title="Edit terms">
            <IconButton
              size="small"
              onClick={() => setExpanded(true)}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <EditIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Expanded editor — only renders when expanded. Replaces the summary
          card above; the "Done" link in the header collapses back. */}
      {expanded && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 1,
          }}
        >
          <Term
            k="Reply by"
            displayValue={formatDate(v.replyByDate)}
            buyerSet={value.replyByDate !== undefined}
            editing={editingField === 'replyByDate'}
            onEdit={() => setEditingField('replyByDate')}
            editor={
              <TextField
                type="date"
                size="small"
                autoFocus
                value={v.replyByDate || ''}
                onChange={(e) => set('replyByDate', e.target.value)}
                onBlur={() => setEditingField(null)}
                inputProps={{ style: { fontSize: '0.82rem', padding: '6px 8px' } }}
                fullWidth
              />
            }
          />
          <Term
            k="Validity"
            displayValue={`${v.validityDays} days`}
            buyerSet={value.validityDays !== undefined}
            editing={editingField === 'validityDays'}
            onEdit={() => setEditingField('validityDays')}
            editor={
              <Select
                size="small"
                autoFocus
                defaultOpen
                value={v.validityDays ?? ''}
                onChange={(e) => set('validityDays', Number(e.target.value))}
                onClose={() => setEditingField(null)}
                fullWidth
                sx={{ fontSize: '0.82rem' }}
              >
                {VALIDITY_OPTIONS.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d} days
                  </MenuItem>
                ))}
              </Select>
            }
          />
          <Term
            k="Currency"
            displayValue={v.currency}
            buyerSet={value.currency !== undefined}
            editing={editingField === 'currency'}
            onEdit={() => setEditingField('currency')}
            editor={
              <Select
                size="small"
                autoFocus
                defaultOpen
                value={v.currency ?? ''}
                onChange={(e) => set('currency', e.target.value)}
                onClose={() => setEditingField(null)}
                fullWidth
                sx={{ fontSize: '0.82rem' }}
              >
                {CURRENCY_OPTIONS.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            }
          />
          <Term
            k="Incoterms"
            displayValue={v.incoterms}
            buyerSet={value.incoterms !== undefined}
            editing={editingField === 'incoterms'}
            onEdit={() => setEditingField('incoterms')}
            editor={
              <Select
                size="small"
                autoFocus
                defaultOpen
                value={v.incoterms ?? ''}
                onChange={(e) => set('incoterms', e.target.value)}
                onClose={() => setEditingField(null)}
                fullWidth
                sx={{ fontSize: '0.82rem' }}
              >
                {INCOTERMS_OPTIONS.map((i) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ))}
              </Select>
            }
          />
          <Term
            k="Payment"
            displayValue={v.paymentTerms}
            buyerSet={value.paymentTerms !== undefined}
            editing={editingField === 'paymentTerms'}
            onEdit={() => setEditingField('paymentTerms')}
            editor={
              <Select
                size="small"
                autoFocus
                defaultOpen
                value={v.paymentTerms ?? ''}
                onChange={(e) => set('paymentTerms', e.target.value)}
                onClose={() => setEditingField(null)}
                fullWidth
                sx={{ fontSize: '0.82rem' }}
              >
                {PAYMENT_OPTIONS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            }
          />
        </Box>
      )}
    </Box>
  );
};

interface TermProps {
  k: string;
  displayValue: string;
  buyerSet: boolean;
  editing: boolean;
  onEdit: () => void;
  editor: React.ReactNode;
}

const Term: React.FC<TermProps> = ({ k, displayValue, buyerSet, editing, onEdit, editor }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 1,
        border: `1px solid ${editing ? theme.palette.primary.main : theme.palette.divider}`,
        borderRadius: 1,
        bgcolor: editing
          ? alpha(theme.palette.primary.main, 0.025)
          : 'background.paper',
        transition: 'border-color 120ms ease, background 120ms ease',
        '&:hover': editing
          ? {}
          : { borderColor: theme.palette.text.secondary },
        cursor: editing ? 'default' : 'pointer',
      }}
      onClick={editing ? undefined : onEdit}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontSize: '0.6rem',
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          color: 'text.secondary',
          fontWeight: 700,
          mb: 0.5,
        }}
      >
        {k}
      </Typography>
      {editing ? (
        editor
      ) : (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.83rem',
              color: buyerSet ? 'text.primary' : 'text.secondary',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {displayValue}
          </Typography>
          <EditIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
        </Stack>
      )}
    </Box>
  );
};

/** Helper to produce the API payload from local state. */
export function toCommercialTermsPayload(value: CommercialTerms): CommercialTerms | undefined {
  if (
    value.replyByDate ||
    value.validityDays ||
    value.currency ||
    value.incoterms ||
    value.paymentTerms
  ) {
    return value;
  }
  return undefined;
}

export default CommercialTermsBar;
